import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';
import { AUDIT_MODULE_KEYS } from './audit-entities';

/** Deckt sich mit `AuditEntry['action']` in `apps/api/src/modules/audit/audit.service.ts`. */
export const auditActionSchema = z.enum(['create', 'update', 'archive', 'delete']);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const auditModuleKeySchema = z.enum(AUDIT_MODULE_KEYS);

/** Aufgelöster Verweis: Klarname statt UUID, plus Ziel-Route, wenn es eine gibt. */
export const auditEntityReferenceSchema = z.object({
  label: z.string(),
  href: z.string().nullable(),
});
export type AuditEntityReference = z.infer<typeof auditEntityReferenceSchema>;

export const auditLogEntrySchema = z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  action: auditActionSchema,
  changes: z.unknown().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),

  // Klarnamen (serverseitig aufgelöst) — die UI zeigt niemals rohe UUIDs.
  /** Deutscher Klarname des Objekttyps, z. B. „Verkaufsbeleg". */
  entityTypeLabel: z.string(),
  moduleKey: auditModuleKeySchema,
  moduleLabel: z.string(),
  /** Klarname des betroffenen Datensatzes; `null`, wenn er nicht (mehr) auflösbar ist. */
  entity: auditEntityReferenceSchema.nullable(),
  /** Anzeigename des handelnden Benutzers. */
  createdByName: z.string().nullable(),
  /**
   * Klarnamen zu allen UUIDs, die in `changes` als Verweis erkannt wurden
   * (Schlüssel = UUID). Damit ersetzt die UI auch dort die kryptischen IDs.
   */
  changeRefs: z.record(z.string(), auditEntityReferenceSchema),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

/**
 * Listen-Query (CLAUDE.md §8) + Audit-spezifische Filter.
 *
 * `q` durchsucht Aktion, Objekt-ID und Objekttyp — technisch wie fachlich:
 * „Bestellung" findet `PurchaseOrder`, weil der Server die deutschen Klarnamen
 * aus dem Objektkatalog mit auflöst.
 *
 * `module` und `entityTypes` sind die beiden Ebenen des Objekt-Filters: erst
 * das Modul (Verkauf, Logistik …), darin optional einzelne Objekttypen. Das
 * ältere `entityType` (Teilstring-Suche) bleibt für Bestands-Links erhalten.
 */
export const auditLogListQuerySchema = listQuerySchema.extend({
  entityType: z.string().optional(),
  entityTypes: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : (Array.isArray(value) ? value : value.split(','))
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0),
    )
    .refine((value) => value === undefined || value.length <= 100, { message: 'Höchstens 100 Objekttypen' }),
  module: auditModuleKeySchema.optional(),
  entityId: z.string().optional(),
  action: auditActionSchema.optional(),
  createdBy: z.string().uuid().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
});
export type AuditLogListQuery = z.infer<typeof auditLogListQuerySchema>;
